import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './useAuth';
import {
  modulePracticeApi,
  type ModuleAnswerResult,
  type ModuleQuestion,
} from '../api/modulePracticeApi';
import type { ModuleFamily } from '../config/moduleCatalog';
import { isEditorRole } from '../types/auth';
import { nextStreak, bestStreakSoFar } from '../utils/streak';

/**
 * Session state for one practice module (feature 023, US5).
 *
 * There is no server-side session: position resumes because the pool order is deterministic
 * per user per module, so "where am I" is derived from answer history (FR-035). This hook
 * therefore only holds what is genuinely per-visit — the current question, the answer just
 * given, the streak, and the tallies the session summary reports when the learner leaves
 * the table.
 */

/**
 * The per-visit tallies behind the Session Summary page. Counted here rather than derived
 * later because nothing about this visit is persisted anywhere — once the page unmounts the
 * numbers are gone, so they have to be handed to the summary at the moment of leaving.
 */
export interface ModulePracticeSessionStats {
  /** Answers submitted this visit (a graded submission, not a hand merely seen). */
  answered: number;
  correct: number;
  incorrect: number;
  /**
   * Hands passed on this visit. Deliberately NOT folded into `answered` — a skip is not a
   * wrong answer, and counting it as one would drag the accuracy the summary reports down for
   * a learner who simply moved past a hand.
   */
  skipped: number;
  /**
   * The longest WINNING run reached this visit. Losing runs never qualify: a "best" of −3
   * would be a personal worst, so the floor stays 0 and a visit with no correct answer in a
   * row reports 0.
   */
  bestStreak: number;
}

/**
 * One question as this visit left it — what "Previous" puts back on screen (client request,
 * 2026-08-03).
 *
 * The server has no notion of going back: the pool is served strictly forward through `/next`,
 * and an answered hand is excluded from it, so asking for a hand already dealt with would deal a
 * different one. The trail is therefore held here, for the length of the visit only. That is not
 * a shortcut around a missing endpoint — stepping back is read-only, so there is nothing to ask
 * the server for that it has not already sent.
 *
 * Multi-step hands put each STEP in the trail, because each step is a question the learner
 * answered separately and "Previous" from step 2 means step 1, not the hand before.
 */
interface VisitedQuestion {
  question: ModuleQuestion;
  /** The graded verdict, or null while the question is open — or if it was skipped. */
  result: ModuleAnswerResult | null;
  /**
   * The wire token submitted (`yes`, `no:pass`, `no:other:<bid>`, or a bare call), kept so a
   * revisited verdict can echo the learner's own call back at them. The answer flow's copy dies
   * with the pads, and it only ever holds the LIVE question's answer.
   */
  answer: string | null;
  /** True once the hand was passed on without a grade. */
  skipped: boolean;
}

/**
 * Which request the on-screen error came from — what "Try again" repeats (spec 030). A failed
 * deal fetches again; a failed skip skips again; a failed submit only clears the message, because
 * the pads are still up and the learner answers again themselves.
 */
type FailedAction = 'load' | 'submit' | 'skip';

export interface ModulePracticeSession {
  question: ModuleQuestion | null;
  result: ModuleAnswerResult | null;
  /**
   * The current RUN this visit: positive for consecutive correct answers, negative for
   * consecutive wrong ones, restarting at ±1 whenever the direction changes. It answers "how
   * many in a row, right now" — which is why it never carries a broken run's value forward.
   */
  streak: number;
  /**
   * The running TOTAL this visit: +1 per correct answer, −1 per wrong one, accumulating
   * through zero. Unlike the streak it survives a change of direction, so it is the measure of
   * how the visit is going overall (client decision, 2026-07-21).
   */
  score: number;
  /** Per-visit tallies for the session summary. See `ModulePracticeSessionStats`. */
  sessionStats: ModulePracticeSessionStats;
  loading: boolean;
  /**
   * What went wrong with the last request, or null. A hand already on screen stays there while
   * this is set — the page shows it as a banner over the table, not in place of it.
   */
  error: string | null;
  /** True when the pool has no unseen questions left. */
  exhausted: boolean;
  /** True while a NEXT question is in flight — the current one stays on screen meanwhile. */
  advancing: boolean;
  /** True while a skip is in flight (the request plus the replacement question's fetch). */
  skipping: boolean;
  /**
   * The wire token submitted for the question on screen, or null while it is open. Feeds the
   * verdict's "you called" echo — for a revisited question the answer flow no longer holds it.
   */
  submittedAnswer: string | null;
  /** True when the question on screen is one the learner stepped BACK to: read-only. */
  isReviewing: boolean;
  /** True when the question on screen was passed on rather than answered. */
  wasSkipped: boolean;
  /** False at the first question of the visit, where there is nothing behind. */
  canGoPrevious: boolean;
  submit: (answer: string) => Promise<void>;
  next: () => Promise<void>;
  /** Records the current hand as skipped and deals the next one. Never grades. */
  skip: () => Promise<void>;
  /** Back to the question already dealt with. Read-only, and never touches the server. */
  previous: () => void;
  /**
   * Repeats whatever failed (spec 030): deals again after a failed first load, Next or the fetch
   * that follows a skip; skips again after a failed skip; clears the message after a failed submit.
   */
  retry: () => Promise<void>;
}

export function useModulePracticeSession(
  family: ModuleFamily,
  topicKey: string,
  moduleSlug: string
): ModulePracticeSession {
  // The visit's trail and where in it the learner is standing. ONE piece of state, not two: the
  // cursor is only ever meaningful against a particular length of trail, and appending a question
  // moves both — split, the append's new index would have to be read out of the other updater's
  // result, which React StrictMode double-invokes.
  const [trail, setTrail] = useState<{ items: VisitedQuestion[]; cursor: number }>({
    items: [],
    cursor: 0,
  });
  // One object rather than six pieces of state: `bestStreak` is a function of the NEXT streak
  // value, which is only knowable inside the streak's own updater. Splitting them would mean
  // reading one updater's result from another — which React StrictMode double-invokes, so the
  // tallies would drift. A single functional update keeps every number derived from one snapshot.
  const [visit, setVisit] = useState({
    streak: 0,
    score: 0,
    answered: 0,
    correct: 0,
    incorrect: 0,
    skipped: 0,
    bestStreak: 0,
  });
  const [loading, setLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);
  const [skipping, setSkipping] = useState(false);
  // The message and the request it came from move together, so they are one piece of state.
  const [failure, setFailure] = useState<{ message: string; action: FailedAction } | null>(null);
  const [exhausted, setExhausted] = useState(false);

  // The `?id=` locator: an editor/admin URL param that jumps straight to one tournament hand by
  // its tableresult id, bypassing the pool order. The id is only ever shown to editors/admins,
  // so only they may locate by it — for everyone else the param is ignored and the normal pool
  // is served. It applies to the FIRST question only; "Next hand" resumes the ordinary flow.
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isEditor = user?.role === 'Admin' || isEditorRole(user?.role);
  const idParam = searchParams.get('id');
  const parsedId = idParam !== null ? Number.parseInt(idParam, 10) : NaN;
  const locatorId = isEditor && Number.isInteger(parsedId) ? parsedId : null;

  // Where the learner is standing, and what that question has been through. The trail's LAST
  // entry is the live question; anything behind it is being revisited and is read-only.
  const entry = trail.items[trail.cursor] ?? null;
  const isReviewing = trail.cursor < trail.items.length - 1;
  const question = entry?.question ?? null;
  const result = entry?.result ?? null;
  // A live hand already passed on, still on screen because the deal that follows the skip failed.
  // It must not be graded or skipped a second time — "Try again" / "Next hand" deal past it.
  const liveSkipped = !isReviewing && !!entry?.skipped;
  const hasDealt = trail.items.length > 0;

  /** Puts a newly dealt question at the head of the trail and stands the learner on it. */
  const appendQuestion = useCallback((q: ModuleQuestion) => {
    setTrail(t => ({
      items: [...t.items, { question: q, result: null, answer: null, skipped: false }],
      cursor: t.items.length,
    }));
  }, []);

  /** Records something about the LIVE question — the only one anything can happen to. */
  const updateLive = useCallback((patch: Partial<VisitedQuestion>) => {
    setTrail(t => ({
      ...t,
      items: t.items.map((e, i) => (i === t.items.length - 1 ? { ...e, ...patch } : e)),
    }));
  }, []);

  /**
   * Fetches the next question.
   *
   * `isFirst` is what stops "Next hand" from flickering. The first load has nothing to show,
   * so it raises `loading` and the page renders "Dealing…". Every later load keeps the
   * current question mounted until the new one arrives — previously it raised `loading` too,
   * which tore down the whole table and rebuilt it, and read as a full page reload.
   */
  const load = useCallback(
    async (isFirst: boolean) => {
      if (isFirst) setLoading(true);
      else setAdvancing(true);
      setFailure(null);
      try {
        const q =
          isFirst && locatorId !== null
            ? await modulePracticeApi.getById(family, topicKey, moduleSlug, locatorId)
            : await modulePracticeApi.getNext(family, topicKey, moduleSlug);
        setExhausted(q.exhausted);
        // Appended only once the replacement is in hand, so the outgoing question never renders
        // for a frame with its answer stripped off. The first load REPLACES the trail — it is a
        // new module, so the previous one's hands are not behind this one.
        if (!q.exhausted) {
          if (isFirst) {
            setTrail({ items: [{ question: q, result: null, answer: null, skipped: false }], cursor: 0 });
          } else {
            appendQuestion(q);
          }
        }
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        // Anything but the locator's 404 — a broken tournament-hand link (500 HANDS_UNAVAILABLE)
        // included — is a retryable fault and says so. It is never reported as exhaustion:
        // `exhausted` is only ever set from a pool that was actually fetched (spec 030).
        setFailure({
          message:
            isFirst && locatorId !== null && status === 404
              ? `No hand with ID ${locatorId} is part of this module.`
              : 'This practice module could not be loaded. Please try again.',
          action: 'load',
        });
        // The trail and the tallies are left alone: the hand on screen stays there, and
        // discarding the visit would throw away hands the learner could still step back through
        // once a retry succeeds.
      } finally {
        setLoading(false);
        setAdvancing(false);
      }
    },
    [family, topicKey, moduleSlug, locatorId, appendQuestion]
  );

  useEffect(() => {
    void load(true);
  }, [load]);

  const submit = useCallback(
    async (answer: string) => {
      // A revisited question is read-only — nothing is re-graded, so the score and the streak
      // cannot be moved by walking back and forth. The page shows its verdict rather than the
      // pads, so this is a backstop rather than the gate.
      if (!question || isReviewing || liveSkipped) return;
      setFailure(null);
      try {
        const r = await modulePracticeApi.submitAnswer(family, topicKey, moduleSlug, {
          partnershipHandId: question.partnershipHandId,
          answer,
          stepIndex: question.stepIndex,
        });
        // The verdict AND the token that earned it go into the trail, so stepping back to this
        // question later replays both.
        updateLive({ result: r, answer });
        // Two different measures, deliberately kept apart (client decision, 2026-07-21).
        //
        // The streak runs both ways — consecutive wrong answers count DOWN rather than parking
        // at zero — but a change of direction RESTARTS at ±1. Carrying a broken run's value
        // forward would make it claim something untrue: one right answer after three wrong is
        // "1 in a row", not "-2 in a row".
        setVisit((v) => {
          const streak = nextStreak(v.streak, r.isCorrect);
          return {
            ...v,
            streak,
            // The score is the one that accumulates through zero, which is what makes it
            // survive the change of direction the streak deliberately discards.
            score: v.score + (r.isCorrect ? 1 : -1),
            answered: v.answered + 1,
            correct: v.correct + (r.isCorrect ? 1 : 0),
            incorrect: v.incorrect + (r.isCorrect ? 0 : 1),
            // Winning-runs-only; see bestStreakSoFar.
            bestStreak: bestStreakSoFar(v.bestStreak, streak),
          };
        });
        if (r.exhausted) setExhausted(true);
      } catch {
        setFailure({ message: 'Your answer could not be recorded. Please try again.', action: 'submit' });
      }
    },
    [family, topicKey, moduleSlug, question, isReviewing, liveSkipped, updateLive]
  );

  const next = useCallback(async () => {
    // Coming forward out of a step back: the question ahead is already in the trail, so this is
    // a move along it and not a deal. It must be checked FIRST — a revisited multi-step question
    // still carries its `nextStep`, and re-appending that would put a duplicate in the trail.
    if (isReviewing) {
      setTrail(t => ({ ...t, cursor: Math.min(t.items.length - 1, t.cursor + 1) }));
      return;
    }
    // Multi-step hands advance WITHIN the hand until the server stops sending a next step;
    // only then do we ask for a new hand.
    if (result?.nextStep) {
      appendQuestion(result.nextStep);
      return;
    }
    if (exhausted) return;
    await load(false);
  }, [isReviewing, result, exhausted, load, appendQuestion]);

  /**
   * Back one question. Pure trail movement — no request, nothing re-graded, and the visit's
   * tallies are untouched: the numbers describe what the learner did, not where they are
   * standing. Inert at the first question of the visit.
   */
  const previous = useCallback(() => {
    setTrail(t => ({ ...t, cursor: Math.max(0, t.cursor - 1) }));
  }, []);

  /**
   * Skip: record the hand as seen-but-ungraded, then deal the next one. It moves ONLY the
   * `skipped` tally — passing on a hand is not a wrong answer, so it leaves the streak, the
   * score and `answered` (which counts graded submissions) untouched. Counted only once the
   * request succeeds, so a failed skip the learner retries cannot tally twice. A skip is only
   * offered while the hand is unanswered, so an already-graded hand is guarded out here too.
   */
  const skip = useCallback(async () => {
    if (!question || result || isReviewing || liveSkipped) return;
    setSkipping(true);
    setFailure(null);
    try {
      try {
        await modulePracticeApi.skip(family, topicKey, moduleSlug, question.partnershipHandId);
      } catch {
        setFailure({ message: 'This hand could not be skipped. Please try again.', action: 'skip' });
        return;
      }
      setVisit((v) => ({ ...v, skipped: v.skipped + 1 }));
      // Marked in the trail so stepping back to it says "Skipped" rather than reopening the pads
      // on a hand that has already been passed on.
      updateLive({ skipped: true });
      // Same fetch "Next hand" makes, so the exhausted marker is handled identically. If it fails,
      // the skip itself is already recorded: `load` reports the failure, the tally stands, and a
      // retry deals the next hand rather than skipping this one again.
      await load(false);
    } finally {
      setSkipping(false);
    }
  }, [family, topicKey, moduleSlug, question, result, isReviewing, liveSkipped, updateLive, load]);

  const retry = useCallback(async () => {
    const action = failure?.action ?? 'load';
    if (action === 'submit') {
      // The pads are still up: the learner answers again, and that submit is the retry.
      setFailure(null);
      return;
    }
    if (action === 'skip') {
      await skip();
      return;
    }
    await load(!hasDealt);
  }, [failure, skip, load, hasDealt]);

  return {
    question,
    result,
    streak: visit.streak,
    score: visit.score,
    sessionStats: {
      answered: visit.answered,
      correct: visit.correct,
      incorrect: visit.incorrect,
      skipped: visit.skipped,
      bestStreak: visit.bestStreak,
    },
    loading,
    advancing,
    skipping,
    error: failure?.message ?? null,
    exhausted,
    submittedAnswer: entry?.answer ?? null,
    isReviewing,
    wasSkipped: !!entry?.skipped,
    canGoPrevious: trail.cursor > 0,
    submit,
    next,
    skip,
    previous,
    retry,
  };
}
